import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { ChangeEvent, useCallback, useState } from 'react';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import * as R from 'Router/routes';
import { isAlreadyExistErrorMsg } from 'utils/errorMessageValidation';
import { applyAll, empty, notPhoneNumber } from 'utils/validations';
import { Button, Input, Link, Typography } from 'components';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import {
  formatBackendValidationErrors,
  PHONE_MASK,
  ValidationError,
} from 'utils';
import { usePhoneVerification /* useRecaptcha */ } from 'hooks/webapi';
import { PREFIX_PHONE } from '../../../constants';
import styles from './PhoneChange.module.scss';
import { useTranslation } from 'react-i18next';

type FormValues = {
  mobilePhone: string;
};

export default function PhoneChange() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { changePhoneWithVerification } = usePhoneVerification();
  const [isFramedError, setIsFramedError] = useState(false);
  // const handleRecaptchaVerify = useRecaptcha('/client/phone');
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (
      { mobilePhone }: FormValues,
      formikHelpers: FormikHelpers<FormValues>,
    ) => {
      try {
        setPending(true);
        // const recaptchaToken = await handleRecaptchaVerify();
        const mobilePhoneSanitized = mobilePhone
          .replace(/ /g, '')
          .replace(PREFIX_PHONE, '');

        await changePhoneWithVerification(
          mobilePhoneSanitized,
          /* recaptchaToken */
        );

        navigate(R.PHONE_VERIFICATION);
      } catch (e: unknown) {
        if (e instanceof ValidationError && e?.fieldErrors?.length) {
          const isAlreadyExistInDbError = isAlreadyExistErrorMsg(e);
          setIsFramedError(isAlreadyExistInDbError);
          const errors = formatBackendValidationErrors(
            e.fieldErrors,
            'mobilePhone',
          );
          formikHelpers.setErrors(errors);
        }
      } finally {
        setPending(false);
      }
    },
    [changePhoneWithVerification /* handleRecaptchaVerify */, navigate],
  );

  const validateForm = useCallback(({ mobilePhone }: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    errors.mobilePhone = applyAll(
      empty,
      notPhoneNumber,
    )(mobilePhone.replace(/ /g, '').replace(PREFIX_PHONE, ''));

    const hasAnyError = Object.values(errors).filter(Boolean).length > 0;
    setDisabled(hasAnyError);

    return hasAnyError ? errors : {};
  }, []);

  const formik = useFormik({
    initialValues: {
      mobilePhone: '',
    },
    onSubmit: handleSubmit,
    validate: validateForm,
  });

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      setIsFramedError(false);
      formik.handleChange(event);

      // Validate phone
      const phoneFilled = !event.target.value.includes('_');
      if (phoneFilled) {
        await formik.setTouched({ mobilePhone: true });
        await formik.validateForm();
      }
    },
    [formik],
  );

  return (
    <>
      <Helmet>
        <title>{t('phone_verification:html_title')}</title>
      </Helmet>

      <div className={styles['box-wrapper']}>
        <Typography
          className={styles.title}
          size="xl"
          kind="h1"
          dataTest="phoneChange-title"
        >
          {t('phone_verification:change_number')}
        </Typography>
        <Typography
          className={styles.message}
          size="s"
          dataTest="phoneChange-message"
        >
          {t('phone_verification:insert_number')}
        </Typography>

        <form className={styles.form} onSubmit={formik.handleSubmit}>
          <Input
            type="tel"
            name="mobilePhone"
            autoComplete="tel"
            inputMode="tel"
            className={styles['mobile-phone-input']}
            value={formik.values.mobilePhone}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            isTouched={formik.touched.mobilePhone}
            placeholder={t('phone_verification:mobile')}
            error={formik.errors.mobilePhone}
            isFramedError={Boolean(formik.errors.mobilePhone) && isFramedError}
            mask={PHONE_MASK}
            guide
            dataTest="phoneChange-phone"
          />
          <Button
            dataTest="phoneChange-btn-submit"
            disabled={!formik.touched.mobilePhone || disabled}
            className={styles.continue}
            pending={pending}
            type="submit"
          >
            {t('common:continue')}
          </Button>
          <div>
            <Link
              className={styles.link}
              to={R.PHONE_VERIFICATION}
              dataTest="phoneChange-phone-link"
            >
              <ChevronIcon
                className={styles.chevron}
                data-test="phoneChange-chevron-icon"
              />
              {t('common:back')}
            </Link>
          </div>
        </form>
      </div>
    </>
  );
}
